import { Component, ChangeDetectionStrategy, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, filter, map, of, startWith, switchMap, timer } from 'rxjs';

const ROUTE_LOADER_DELAY_MS = 150;
import { FirebaseAuthService } from './services/auth/firebase-auth.service';
import { MembershipService } from './services/auth/membership.service';
import { UserProfileService } from './services/auth/user-profile.service';
import { ThemeService } from './services/auth/theme.service';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { PatientSearch, MedicationSearch } from './services/search_utils/interconsulta-search';
import { PathConstants } from './constants/PathConstants';
import { resolveAppEntryPath } from './services/auth/auth-navigation';
import { FeatureFlagService } from './services/feature-flag.service';
import { AppVersionService, isStaleChunkError } from './services/app-version.service';
import { ClinicThemeService } from './services/clinic-theme.service';
import { BreakpointService } from './services/layout/breakpoint.service';
import {
  NewBusinessDialogComponent,
  NewBusinessDialogData,
} from './components/new-business-dialog/new-business-dialog.component';
import { MatDividerModule } from '@angular/material/divider';
import { GuideAnchorDirective } from './guides/guide-anchor.directive';
import { GuideOverlayComponent } from './guides/guide-overlay.component';
import { GuideService } from './guides/guide.service';
import { KioskService } from './services/kiosk.service';

function isAuthUrl(url: string): boolean {
  return (
    url.startsWith('/login') ||
    url.startsWith('/signup') ||
    url.startsWith('/onboarding') ||
    url.startsWith('/verificar-correo') ||
    url.startsWith('/no-business') ||
    url.startsWith('/invitacion') ||
    url.startsWith('/search') ||
    url.startsWith('/teleconsulta/unirse')
  );
}

function safeAreaTopInset(): number {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;padding-top:env(safe-area-inset-top)';
  document.body.appendChild(probe);
  const inset = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return inset;
}

@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.css'],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    SideMenuComponent,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    GuideAnchorDirective,
    GuideOverlayComponent,
  ],
})
export class AppComponent {
  firebase = inject(FirebaseAuthService);
  membership = inject(MembershipService);
  readonly kiosk = inject(KioskService);
  userProfile = inject(UserProfileService);
  theme = inject(ThemeService);
  layout = inject(BreakpointService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private patientSearch = inject(PatientSearch);
  private medicationSearch = inject(MedicationSearch);
  private flags = inject(FeatureFlagService);
  private readonly guide = inject(GuideService);
  private readonly sideNavRef = viewChild<MatSidenav>('sideNav');

  readonly leavingImpersonation = signal(false);
  /** Toolbar height plus the iOS notch inset, so the side menu opens below the app bar. */
  readonly sideNavTopGap = computed(
    () => 60 + safeAreaTopInset() + (this.firebase.isLoggedIn() && this.firebase.impersonation() ? 44 : 0)
  );

  async leaveImpersonation(): Promise<void> {
    if (this.leavingImpersonation()) {
      return;
    }
    this.leavingImpersonation.set(true);
    try {
      await this.firebase.stopImpersonation();
    } catch (err) {
      console.error('[App] stop impersonation failed', err);
      this.leavingImpersonation.set(false);
    }
  }

  constructor() {
    const versions = inject(AppVersionService);
    versions.start();
    inject(ClinicThemeService);
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (isStaleChunkError(event.error)) {
          versions.reload(event.url);
        }
      });
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.holdKiosk(event.urlAfterRedirects));
    let menuOpenedForGuide = false;
    effect(() => {
      const openForGuide = this.guide.step()?.openMenu === true;
      const nav = this.sideNavRef();
      // close()/open() read the drawer's opened signal. Leave that untracked so a
      // normal menu click does not re-run this effect and snap the drawer shut.
      untracked(() => {
        if (!nav) {
          return;
        }
        if (openForGuide) {
          menuOpenedForGuide = true;
          void nav.open();
        } else if (menuOpenedForGuide) {
          menuOpenedForGuide = false;
          void nav.close();
        }
      });
    });
  }

  readonly canCreateBusiness = computed(() => this.membership.canCreateBusiness());
  readonly businessMenuEnabled = computed(
    () => !this.kiosk.locked() && (this.membership.hasMultipleBusinesses() || this.canCreateBusiness())
  );

  /** A locked tablet stays on registration, even if the address bar changes. */
  private holdKiosk(url: string): void {
    if (!this.firebase.isLoggedIn()) {
      if (this.kiosk.locked()) {
        this.kiosk.unlock();
      }
      return;
    }
    if (this.kiosk.locked() && !url.startsWith(PathConstants.PATIENT_REGISTER_PATH)) {
      void this.router.navigateByUrl(PathConstants.PATIENT_REGISTER_PATH);
    }
  }

  onSelectBusiness(businessId: string): void {
    void this.switchBusiness(businessId);
  }

  openNewBusiness(): void {
    const data: NewBusinessDialogData = {
      existingBusinessNames: this.membership.memberships().map((m) => m.businessName),
    };
    this.dialog
      .open<NewBusinessDialogComponent, NewBusinessDialogData, string>(
        NewBusinessDialogComponent,
        { data, width: '520px', maxWidth: '95vw', autoFocus: 'first-tabbable' }
      )
      .afterClosed()
      .subscribe((businessId) => {
        if (businessId) {
          void this.switchBusiness(businessId);
        }
      });
  }

  toggleTheme(): void {
    this.theme.setPreference(this.theme.preference() === 'dark' ? 'light' : 'dark');
  }

  onSideNavOpenedChange(): void {
    this.guide.refresh();
  }

  onSideNavClosing(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.classList.contains('sidenav-button')) {
      active.blur();
    }
  }

  private async switchBusiness(businessId: string): Promise<void> {
    const changed = await this.membership.selectBusiness(businessId);
    if (!changed) {
      return;
    }

    this.patientSearch.clear();
    this.medicationSearch.clear();
    this.dialog.closeAll();

    let url = this.router.url;
    const clinicalRoute =
      url.startsWith(PathConstants.PATIENTS_PATH) ||
      url.startsWith(PathConstants.MEDICATIONS_PATH) ||
      url.startsWith(PathConstants.CITAS_PATH) ||
      url.startsWith(PathConstants.PLANTILLAS_PATH) ||
      url.startsWith(PathConstants.APPOINTMENTS_PATH);
    if (clinicalRoute && !this.membership.canManageClinicalRecords()) {
      url = resolveAppEntryPath(this.membership);
    } else if (
      url.startsWith(PathConstants.APPOINTMENTS_PATH) &&
      !this.membership.canAccessAppointments()
    ) {
      url = resolveAppEntryPath(this.membership);
    } else if (
      url.startsWith(PathConstants.CONSULTORIO_PATH) &&
      !this.membership.canManageSelectedBusiness()
    ) {
      url = resolveAppEntryPath(this.membership);
    } else if (
      url.startsWith(PathConstants.FACTURACION_PATH) &&
      !this.flags.isEnabled('invoicing')
    ) {
      url = resolveAppEntryPath(this.membership);
    } else if (
      url.startsWith(PathConstants.TELECONSULTA_PATH) &&
      (!this.flags.isEnabled('teleconsulta') || !this.membership.canManageClinicalRecords())
    ) {
      url = resolveAppEntryPath(this.membership);
    }
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    await this.router.navigateByUrl(url);
  }

  /**
   * True while a navigation (guards, lazy chunk) is pending. Shown after a short delay so
   * fast tab switches don't flash; starts true so the first load shows it immediately.
   */
  readonly routeLoading = toSignal(
    this.router.events.pipe(
      filter(
        (event) =>
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
      ),
      map((event) => event instanceof NavigationStart),
      distinctUntilChanged(),
      switchMap((loading) => (loading ? timer(ROUTE_LOADER_DELAY_MS).pipe(map(() => true)) : of(false)))
    ),
    { initialValue: true }
  );

  readonly isAuthLayout = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => isAuthUrl(event.urlAfterRedirects)),
      startWith(isAuthUrl(this.router.url))
    ),
    { initialValue: isAuthUrl(this.router.url) }
  );

  title = 'Interconsulta';
}
